import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  FormControlLabel,
  Button,
  TextField,
  Checkbox,
  Box,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material/";

import { debounce, flow, isArray, isNumber, isObject } from "lodash";

// ICONS
import {
  ArrowForwardIos,
  Autorenew,
  Close,
  Delete,
  Update,
} from "@mui/icons-material";

import LinearProgress from "@mui/material/LinearProgress";
import { CustomIcons, formatDate } from "../../Components/helpers/Helpers";
import { CustomButtonPlusIcon } from "../../Components/Common/Design/CustomButtonPlusIcon";
import {
  DocsContext,
  PermissionContext,
} from "../../Components/Context/Context";
import LoadingPage from "../../Components/Common/Loading/LoadingPage";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import api from "../../api/axios";
import { useNavigate } from "react-router-dom";
import { COLORS } from "./muiStyles/muiStyles";

// ======================================================
// COMPONENT: ConfigControlSection
// Descripción: Controla el progreso y actualización de
// estados de expediente, así como la eliminación de casos.
// ======================================================
const ConfigControlSection = ({
  dataCase,
  caseId,
  setDataCase,
  progress,
  onFinish,
  setUpdatedCase,
  updatedCase,
}) => {
  const navigate = useNavigate();

  const Swal = require("sweetalert2");

  const { can } = useContext(PermissionContext);
  const canChange = can("cases", "change");
  const canDelete = can("cases", "delete");
  // -------------------------------
  // ESTADOS LOCALES
  // -------------------------------
  const { docsInfo } = useContext(DocsContext);
  const [revertCaseFlow, setRevertCaseFlow] = useState(null);
  const [activeStepStatus, setActiveStepStatus] = useState(0);
  const [activeStepDelete, setActiveStepDelete] = useState(0);
  const [openModalStatus, setOpenModalStatus] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorDoc, setErrorDocs] = useState(false);
  const [docsDecision, setDocsDecision] = useState(0);
  const [dateTrialTime, setDateTrialTime] = React.useState(null);

  const stepsChangeStatus = ["Inicio", "Finalizar"];
  const stepsDelete = ["Inicio", "Documentación", "Eliminar Expediente"];

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md")); // true si pantalla es md o menos

  // FUNCION: Actualizar estados de peritaje y apud
  const handleConfigPanel = async () => {
    const urlEditState = `${process.env.REACT_APP_API_URL}/cases/updatecontrolpanel`;

    const formData = new FormData();
    formData.append("case_id", Number(caseId));
    formData.append(
      "apud_acta",
      dataCase.apud_acta == null ? null : Number(dataCase.apud_acta),
    );
    formData.append(
      "peritaje",
      dataCase.peritaje == null ? null : Number(dataCase.peritaje),
    );
    formData.append(
      "expiration",
      dataCase.expiration == null ? null : Number(dataCase.expiration),
    );

    // Confirmación
    const result = await Swal.fire({
      title: "¿Desea cambiar el estado?",
      text: "Esta acción actualizará la configuración del expediente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Sí, cambiar",
      cancelButtonText: "Volver",
    });

    if (!result.isConfirmed) return;

    // Loading
    Swal.fire({
      title: "Actualizando estado...",
      text: "Por favor espere",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await api.post(urlEditState, formData);

      Swal.fire({
        title: "¡Estado actualizado!",
        icon: "success",
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Aceptar",
      });
    } catch (error) {
      console.error(error);

      Swal.fire({
        title: "Error",
        text: "No se pudo actualizar el estado. Inténtelo nuevamente.",
        icon: "error",
        confirmButtonColor: "#1ab394",
      });
    }
  };

  // FUNCION: Avanzar pasos de modal
  // flag = "status" o "eliminar"
  const handleNext = async (flag) => {
    setLoading(true);

    // ---------- CASO: Cambiar estado ----------
    if (flag === "status") {
      console.log("Entra", activeStepStatus);

      // Avanzar paso
      setActiveStepStatus((prev) =>
        Math.min(prev + 1, stepsChangeStatus.length - 1),
      );
      // console.log(revertCaseFlow);
      // console.log(dataCase.tasks);

      // Solo en el primer paso, hacer revert de status
      if (activeStepStatus === 0 && revertCaseFlow) {
        const tasksToDelete = [];
        let principalTask = null;

        // Filtrar tareas a eliminar y principal
        dataCase.tasks.forEach((task) => {
          if (task?.cases_flow_relation?.order > revertCaseFlow.order) {
            tasksToDelete.push(task.id);
          } else if (
            task?.cases_flow_relation?.order === revertCaseFlow.order
          ) {
            principalTask = task;
          }
        });

        const formData = new FormData();
        formData.append("tasks_to_delete", JSON.stringify(tasksToDelete));
        formData.append("principal_task", JSON.stringify(principalTask));
        formData.append("case_id", Number(caseId));

        console.log(tasksToDelete);
        try {
          const url = `${process.env.REACT_APP_API_URL}/casestasks/revertcasestatus`;
          const { data } = await api.post(url, formData);
          console.log(data);
          setRevertCaseFlow(null);
          setUpdatedCase(!updatedCase);
        } catch (error) {
          console.error("Error al revertir estado del caso:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }

    // ---------- CASO: Eliminar ----------
    else if (flag === "eliminar") {
      console.log("Entra", activeStepDelete);

      // Avanzar paso
      setActiveStepDelete((prev) => Math.min(prev + 1, stepsDelete.length - 1));

      if (activeStepDelete === 0) {
        // Simular retraso antes de continuar
        setTimeout(() => {
          console.log("1S después");
          setLoading(false);
        }, 1000);
      } else if (activeStepDelete === 1) {
        console.log(typeof docsDecision, "docsDecision");

        if ([0, 1].includes(Number(docsDecision))) {
          console.log("Conservar doc");
          await handleDeleteCase(true);
          setUpdatedCase(!updatedCase);
        } else if (docsDecision == 2) {
          console.log("Eliminar doc");

          const docsToDelete = deleteMultipleDocuments();

          if (docsToDelete) {
            console.log("Docs preparados para eliminar:", docsToDelete);
            const responseCase = await handleDeleteCase(true);
            setUpdatedCase(!updatedCase);
            console.log("Case eliminado:", responseCase);
          } else {
            console.warn("No hay documentos para eliminar");
            setLoading(false);
          }
        }
      }
    }
  };

  // FUNCION: Eliminar caso
  const handleDeleteCase = async (flag = false) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("case_id", Number(caseId));
    formData.append("keep_doc", flag);

    if (flag) {
      const url = `${process.env.REACT_APP_API_URL}/cases/delete`;
      try {
        const response = await api.post(url, formData);
        setLoading(false);
        return response;
      } catch (error) {
        console.error(error);
      }
    }
  };

  // FUNCION: Preparar documentos para eliminación múltiple
  const deleteMultipleDocuments = async () => {
    setLoading(true);
    const allDocs = [];

    Object.entries(docsInfo).forEach(([caseId, categories]) => {
      Object.entries(categories).forEach(([category, docsArray]) => {
        docsArray.forEach((doc) => {
          allDocs.push({ doc_id: doc.doc_id || null, path: doc.path });
        });
      });
    });

    // console.log(allDocs);

    if (allDocs?.length > 0) {
      const formData = new FormData();
      formData.append("documents", JSON.stringify(allDocs));

      try {
        const { data } = await api.post(
          `${process.env.REACT_APP_API_URL}/s3/destroymultiple`,
          formData,
        );

        Swal.close();
        if (data.success) {
          setLoading(false);
          return data.success;
        } else if (data?.error?.errorInfo?.length > 0) setErrorDocs(true);
      } catch (error) {
        Swal.close();
        Swal.fire({
          title: "¡Error de conexión!",
          icon: "error",
          text: "No se pudo eliminar los archivos.",
          confirmButtonColor: "#1ab394",
          confirmButtonText: "Continuar",
        });
        console.error(error);
      }
    }
  };

  // FUNCIONES: Retroceder pasos
  const handleBackDelete = () => {
    setActiveStepDelete((prev) => Math.max(prev - 1, 0));
    setDocsDecision(0);
  };
  const handleBackStatus = () =>
    setActiveStepStatus((prev) => Math.max(prev - 1, 0));

  // FUNCION: Finalizar modal
  const handleFinish = (flag) => {
    if (flag == "delete") navigate("/expedientes");
    setUpdatedCase(!updatedCase);

    handleCloseModal();
    onFinish && onFinish();
  };

  // FUNCIONES: Abrir / cerrar modales
  const handleOpenModal = (flag) => {
    if (flag == "status") {
      if (revertCaseFlow?.id) {
        setOpenModalStatus(true);
      } else {
        Swal.fire({
          title: "¡Error!",
          icon: "error",
          text: "Por favor seleccione un estado válido.",
          confirmButtonColor: "#1ab394",
          confirmButtonText: "Volver",
          showDenyButton: false,
        });
      }
    } else if (flag == "eliminar") {
      setOpenModalDelete(true);
    }
  };

  const handleCloseModal = () => {
    setOpenModalStatus(false);
    setOpenModalDelete(false);
    setDocsDecision(0);
    setActiveStepStatus(0);
    setActiveStepDelete(0);
    setRevertCaseFlow(null);
  };

  const filteredOldStates =
    dataCase?.old_states?.filter((element) => {
      const ultimo = dataCase.completeFlow[dataCase.completeFlow.length - 1];
      return element.order !== ultimo.order;
    }) || [];

  const handleChangeTrialDate = async () => {
    if (!dateTrialTime) {
      Swal.fire({
        icon: "warning",
        title: "Fecha requerida",
        text: "Debes seleccionar una fecha y hora válida",
      });
      return;
    }

    const formatedDate = formatDate(dateTrialTime);

    const confirm = await Swal.fire({
      title: "¿Confirmar cambio?",
      text: `La fecha del juicio será actualizada a:\n${dateTrialTime.format(
        "DD/MM/YYYY HH:mm",
      )}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Sí, guardar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    try {
      // Loading
      Swal.fire({
        title: "Guardando fecha...",
        text: "Por favor espera",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
        timer: 2000, //
      });

      await api.post("/cases/update", {
        id: Number(caseId),
        trial_date: formatedDate[0],
      });

      // ✅ Éxito
      await Swal.fire({
        icon: "success",
        title: "Fecha actualizada",
        text: "La fecha del juicio se guardó correctamente",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error al cambiar fecha de juicio:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          "No se pudo guardar la fecha del juicio",
      });
    }
  };

  // ======================================================
  // RENDER PRINCIPAL
  // ======================================================
  return (
    <>
      {/* PROGRESO */}

      {/* ---------------- SECCIÓN IZQUIERDA: Apud acta / Peritaje ---------------- */}
      <Grid item xs={12} md={6} sx={{ p: "8px" }}>
        {canChange && (
          <Box sx={{ background: "#fff", p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Configuración del expediente
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* PERITAJE */}
            <Grid container alignItems="center" spacing={0.5}>
              <Grid item xs={6} md={5} lg={4}>
                <Typography fontSize={13}>¿Con peritaje?</Typography>
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dataCase.peritaje === 0}
                      onChange={() =>
                        setDataCase({
                          ...dataCase,
                          peritaje: dataCase.peritaje === 0 ? null : 0,
                        })
                      }
                    />
                  }
                  label="No"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dataCase.peritaje === 1}
                      onChange={() =>
                        setDataCase({
                          ...dataCase,
                          peritaje: dataCase.peritaje === 1 ? null : 1,
                        })
                      }
                    />
                  }
                  label="Sí"
                />
              </Grid>
            </Grid>

            {/* APUD ACTA */}
            <Grid container alignItems="center" spacing={0.5}>
              <Grid item xs={6} md={5} lg={4}>
                <Typography fontSize={13}>¿Apud acta?</Typography>
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dataCase.apud_acta === 0}
                      onChange={() =>
                        setDataCase({
                          ...dataCase,
                          apud_acta: dataCase.apud_acta === 0 ? null : 0,
                        })
                      }
                    />
                  }
                  label="No"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dataCase.apud_acta === 1}
                      onChange={() =>
                        setDataCase({
                          ...dataCase,
                          apud_acta: dataCase.apud_acta === 1 ? null : 1,
                        })
                      }
                    />
                  }
                  label="Sí"
                />
              </Grid>
            </Grid>

            {/* VENCIMIENTO */}
            <Grid container alignItems="center" spacing={0.5}>
              <Grid item xs={6} md={5} lg={4}>
                <Typography fontSize={13}>¿Vencimiento?</Typography>
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dataCase.expiration === 0}
                      onChange={() =>
                        setDataCase({
                          ...dataCase,
                          expiration: dataCase.expiration === 0 ? null : 0,
                        })
                      }
                    />
                  }
                  label="No"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dataCase.expiration === 1}
                      onChange={() =>
                        setDataCase({
                          ...dataCase,
                          expiration: dataCase.expiration === 1 ? null : 1,
                        })
                      }
                    />
                  }
                  label="Sí"
                />
              </Grid>
            </Grid>

            {/* BOTÓN */}
            <Box mt={2} display="flex" justifyContent="flex-end">
              <CustomButtonPlusIcon
                icon={Autorenew}
                text={isMd ? "" : "Actualizar estados"}
                handleFunction={handleConfigPanel}
                variant="outlined"
                position="start"
              />
            </Box>
          </Box>
        )}
      </Grid>

      {/* ---------------- SECCIÓN DERECHA: Eliminar expediente, Cambio de estado del expediente ---------------- */}
      <Grid item xs={12} md={6} sx={{ p: "8px 8px 8px 0px" }}>
        {canChange && (
          <Box sx={{ background: "#fff", p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Acciones del expediente
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* CAMBIO DE ESTADO */}
            <Grid container spacing={0.5}>
              <Grid item xs={9}>
                <Autocomplete
                  size="small"
                  value={revertCaseFlow}
                  onChange={(e, v) => v && setRevertCaseFlow(v)}
                  options={filteredOldStates}
                  getOptionLabel={(option) =>
                    option.status_name === "Seleccionar"
                      ? option.status_name
                      : `Pendiente ${option.status_name}`
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Retroceder estado del expediente"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3}>
                <CustomButtonPlusIcon
                  icon={Update}
                  position="start"
                  text="Cambiar"
                  fullWidth
                  handleFunction={() => handleOpenModal("status")}
                  sxProps={{ height: "100%" }}
                />
              </Grid>
            </Grid>

            {/* FECHA DE JUICIO */}
            <Grid container spacing={0.5} mt={1}>
              <Grid item xs={9}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="es">
                  <DateTimePicker
                    label="Fecha y hora de juicio"
                    value={
                      dateTrialTime
                        ? dayjs(dateTrialTime)
                        : dataCase?.trial_date
                          ? dayjs(dataCase.trial_date)
                          : null
                    }
                    onChange={(newValue) => setDateTrialTime(newValue)}
                    ampm={false}
                    minutesStep={5}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={3}>
                <CustomButtonPlusIcon
                  icon={Update}
                  handleFunction={handleChangeTrialDate}
                  text="Configurar"
                  position="start"
                  fullWidth
                  sxProps={{ height: "100%" }}
                />
              </Grid>
            </Grid>

            {/* ELIMINAR */}
            {canDelete && (
              <>
                <Divider sx={{ my: 1 }} />
                <CustomButtonPlusIcon
                  color="error"
                  icon={Delete}
                  text="Eliminar expediente"
                  handleFunction={() => handleOpenModal("eliminar")}
                  variant="outlined"
                  position="start"
                  fullWidth
                />
              </>
            )}
          </Box>
        )}
      </Grid>

      {/* ================= MODAL: Cambiar estado ================= */}

      <Dialog
        open={openModalStatus}
        onClose={
          activeStepStatus === stepsChangeStatus.length - 1
            ? null
            : handleCloseModal
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "420px", // altura personalizada
          },
        }}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#0073ff",
            color: "white",
            fontWeight: "700",
            mb: 3,
            p: 2,
          }}>
          Cambio de estado del Expediente
          <IconButton
            color="error"
            aria-label="cerrar"
            onClick={handleCloseModal}
            disabled={activeStepStatus === stepsChangeStatus.length - 1}
            sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Stepper activeStep={activeStepStatus} alternativeLabel>
              {stepsChangeStatus.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box mt={5}>
              {activeStepStatus === 0 && (
                <div>
                  <b>
                    Estás a punto de retroceder el Expediente a un estado
                    anterior.
                  </b>
                  <p>Si estás de acuerdo, da clic en siguiente.</p>
                </div>
              )}
              {activeStepStatus === 1 && (
                <div style={{ position: "relative", height: "170px" }}>
                  {loading ? (
                    <>
                      <p style={{ color: "#0c1e35", textAlign: "center" }}>
                        <em>Actualizando expediente...</em>
                      </p>
                      <LoadingPage
                        isLoading={loading}
                        heightProp={"100px"}
                        widthProp={"100px"}
                      />
                    </>
                  ) : (
                    <Alert severity="success">
                      Expediente actualizado correctamente
                    </Alert>
                  )}
                </div>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ display: "block" }}>
          <Box mt={2} display="flex" justifyContent="space-between">
            <Button
              disabled={
                activeStepStatus === 0 || (!loading && activeStepStatus === 1)
              }
              onClick={handleBackStatus}>
              Atrás
            </Button>
            {activeStepStatus === stepsChangeStatus.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => handleFinish("status")}
                disabled={loading}>
                Finalizar
              </Button>
            ) : (
              <Button variant="contained" onClick={() => handleNext("status")}>
                Enviar
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* ================= MODAL: Eliminar expediente ================= */}
      <Dialog
        open={openModalDelete}
        onClose={
          activeStepDelete === stepsDelete.length - 1 ? null : handleCloseModal
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "550px", // altura personalizada
          },
        }}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#d32f2f",
            color: "white",
            fontWeight: "700",
            mb: 3,
            p: 2,
          }}>
          Eliminar expediente
          <IconButton
            aria-label="cerrar"
            onClick={handleCloseModal}
            disabled={activeStepDelete === stepsDelete.length - 1}
            sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box>
            <Stepper activeStep={activeStepDelete} alternativeLabel>
              {stepsDelete.map((label) => (
                <Step key={label}>
                  <StepLabel
                    slots={{
                      stepIcon: (props) => (
                        <div
                          style={{
                            backgroundColor: "#d32f2f",
                            color: "white",
                            borderRadius: "4px",
                            width: 28,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.8rem",
                          }}>
                          {props.icon}
                        </div>
                      ),
                    }}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box mt={5}>
              {activeStepDelete === 0 && (
                <div>
                  <Alert severity="warning">
                    Estás a punto de eliminar el Expediente completamente.
                  </Alert>
                  <Alert severity="info">
                    Clic en siguiente para continuar.
                  </Alert>
                </div>
              )}
              {activeStepDelete === 1 && (
                <div style={{ position: "relative", height: "170px" }}>
                  {loading ? (
                    <>
                      <p style={{ color: "#0c1e35", textAlign: "center" }}>
                        <em>Verificando datos vinculados al expediente...</em>
                      </p>
                      <LoadingPage
                        isLoading={loading}
                        heightProp={"100px"}
                        widthProp={"100px"}
                      />
                    </>
                  ) : Object.entries(docsInfo).length > 0 ? (
                    <div>
                      <Alert severity="warning">
                        <b
                          style={{
                            color: "rgb(102, 60, 0)",
                            marginRight: "10px",
                          }}>
                          ¡Cuidado!
                        </b>
                        <span>
                          Se ha encontrado documentación asociada al caso.
                        </span>
                      </Alert>
                      <Alert severity="warning">
                        Al clicar en ELIMINAR EXPEDIENTE, toda la información
                        asociada al expediente será eliminada.
                      </Alert>
                      <Alert severity="info">
                        ¿Desea conservar la documentación del caso? En caso de
                        no hacerlo, se perderá para siempre. Por favor
                        seleccione una opción:
                      </Alert>
                      <FormControl
                        sx={{
                          "& .MuiFormControlLabel-label": { fontSize: 14 },
                          mt: 1,
                        }}>
                        <RadioGroup
                          aria-labelledby="radioNewCaseStatus"
                          name="radio-buttons-group"
                          value={docsDecision} // <- controlado por estado
                          onChange={(event) =>
                            setDocsDecision(event.target.value)
                          }>
                          <FormControlLabel
                            value="1"
                            control={<Radio />}
                            label="Conservar documentación."
                          />
                          <FormControlLabel
                            value="2"
                            control={<Radio />}
                            label="Eliminar documentación."
                          />
                        </RadioGroup>
                      </FormControl>
                    </div>
                  ) : (
                    <>
                      <Alert severity="success">
                        No se ha encontrado documentación asociada al
                        expediente, puede proseguir.
                      </Alert>
                      <Alert severity="warning">
                        Al clicar en ELIMINAR EXPEDIENTE, toda la información
                        asociada al expediente será eliminada.
                      </Alert>
                    </>
                  )}
                </div>
              )}
              {activeStepDelete === 2 && (
                <div style={{ position: "relative", height: "170px" }}>
                  {loading ? (
                    <>
                      <p style={{ color: "#0c1e35", textAlign: "center" }}>
                        <em>Eliminando expediente...</em>
                      </p>
                      <LoadingPage
                        isLoading={loading}
                        heightProp={"100px"}
                        widthProp={"100px"}
                      />
                    </>
                  ) : errorDoc ? (
                    <Alert severity="error">
                      Ha ocurrido un error al procesar la solicitud.
                    </Alert>
                  ) : (
                    <Box>
                      <Alert severity="success">
                        Expediente eliminado correctamente, Click en finalizar
                        para terminar el proceso.
                      </Alert>
                    </Box>
                  )}
                </div>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ display: "block" }}>
          <Box mt={2} display="flex" justifyContent="space-between">
            <Button
              disabled={!loading && activeStepDelete === 2}
              onClick={
                activeStepDelete === 0 ? handleCloseModal : handleBackDelete
              }
              variant="outlined"
              color="error">
              {activeStepDelete === 0 ? "Cancelar" : "Atrás"}
            </Button>
            {activeStepDelete === stepsDelete.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => handleFinish("delete")}
                disabled={loading}>
                Finalizar
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => handleNext("eliminar")}
                color={"error"}
                disabled={
                  activeStepDelete === 1 &&
                  Object.entries(docsInfo).length > 0 &&
                  !docsDecision
                }>
                {activeStepDelete === 1 ? "Eliminar Expediente" : "Siguiente"}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfigControlSection;
